/**
 * ポータル: 社内マニュアル（Wiki型＋ファイル型）
 * タイトル検索／全文検索（PDF・Word中身）／タグ検索を分けて提供。既読確認・版数つき。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, Plus, Search, FileText, FileUp, Tag, CheckCircle2, Eye } from 'lucide-react';
import * as api from '../../services/api';
import { Badge, Modal, fieldStyle, labelStyle, fmtDateTime, fileUrl, MarkdownView, EmptyState } from './ui';

interface Manual {
  id: string; kind: string; title: string; body?: string | null;
  file_name: string | null; mime: string | null; size: number | null;
  tags: string[]; category: string | null; require_read: number; status: string; version: number;
  updated_by_name: string | null; created_at: string; updated_at: string;
  my_read: boolean; excerpt?: string; read_count?: number; user_count?: number;
}

export default function PortalManualsPage() {
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } }, []);
  const isAdmin = ['system_admin', 'shore_admin'].includes(user?.user_role ?? '');

  const loc = useLocation();
  // ポータルホームの検索ボックスから ?q= で遷移してきたときは全文検索で開く
  const initialQ = useMemo(() => new URLSearchParams(loc.search).get('q') ?? '', [loc.search]);
  const [rows, setRows] = useState<Manual[]>([]);
  const [q, setQ] = useState(initialQ);
  const [scope, setScope] = useState<'title' | 'full'>(initialQ ? 'full' : 'title');
  const [tag, setTag] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [detail, setDetail] = useState<Manual | null>(null);
  const [reads, setReads] = useState<Array<{ user_id: string; user_name: string; read_at: string | null }> | null>(null);
  const [editor, setEditor] = useState<{ manual: Manual | null } | null>(null);
  const [showFileUp, setShowFileUp] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (q) { params['q'] = q; params['scope'] = scope; }
      if (tag) params['tag'] = tag;
      if (category) params['category'] = category;
      if (isAdmin) params['status'] = 'all';
      const r = await api.get<{ data: Manual[] }>('/portal/manuals', params);
      setRows(r.data ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [q, scope, tag, category, isAdmin]);

  const loadTags = useCallback(() => {
    api.get<{ data: { tags: string[]; categories: string[] } }>('/portal/manuals/tags')
      .then(r => { setTags(r.data.tags ?? []); setCategories(r.data.categories ?? []); })
      .catch(() => { /* noop */ });
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { loadTags(); }, [loadTags]);

  const openDetail = async (id: string) => {
    try {
      const r = await api.get<{ data: Manual }>(`/portal/manuals/${id}`);
      setDetail(r.data);
      setReads(null);
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const markRead = async (m: Manual) => {
    try {
      await api.post(`/portal/manuals/${m.id}/read`, {});
      setDetail(d => d ? { ...d, my_read: true } : d);
      setRows(rs => rs.map(r => r.id === m.id ? { ...r, my_read: true } : r));
    } catch { /* noop */ }
  };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOpen size={20} /> 社内マニュアル
        </h1>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setShowFileUp(true)}>
              <FileUp size={15} /> ファイルを登録
            </button>
            <button className="btn-p" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setEditor({ manual: null })}>
              <Plus size={15} /> ページを作成
            </button>
          </div>
        )}
      </div>

      {/* 検索バー */}
      <div className="card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select style={{ ...fieldStyle, width: 130 }} value={scope} onChange={e => setScope(e.target.value as 'title' | 'full')}>
          <option value="title">タイトル検索</option>
          <option value="full">全文検索</option>
        </select>
        <input style={{ ...fieldStyle, width: 260 }} placeholder={scope === 'full' ? 'PDF・Wordの中身も検索します' : 'タイトルで検索'}
          value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void load(); }} />
        <select style={{ ...fieldStyle, width: 150 }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">カテゴリ: すべて</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="btn-p" onClick={() => void load()}><Search size={14} /></button>
        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
            <Tag size={13} color="#6b7280" />
            {tags.map(t => (
              <button key={t} onClick={() => setTag(tag === t ? '' : t)}
                style={{
                  border: '1px solid', borderColor: tag === t ? '#0f766e' : '#e5e7eb', borderRadius: 999,
                  padding: '3px 12px', fontSize: 12, cursor: 'pointer',
                  background: tag === t ? '#e6f4f1' : '#fff', color: tag === t ? '#0f766e' : '#6b7280', fontWeight: 600,
                }}>
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {loading ? <div className="loading">読み込み中...</div> : rows.length === 0 ? (
          <EmptyState img="/portal/empty-manuals.png" text="該当するマニュアルがありません" />
        ) : (
          <table>
            <thead><tr>
              <th style={{ width: 60 }}>形式</th><th>タイトル</th><th style={{ width: 110 }}>カテゴリ</th>
              <th style={{ width: 180 }}>タグ</th><th style={{ width: 60 }}>版</th>
              <th style={{ width: 140 }}>更新</th><th style={{ width: 90 }}>状態</th>
            </tr></thead>
            <tbody>
              {rows.map(m => (
                <tr key={m.id} style={{ cursor: 'pointer' }} onClick={() => void openDetail(m.id)}>
                  <td>{m.kind === 'file' ? <Badge color="#7c3aed" bg="#ede9fe">ファイル</Badge> : <Badge color="#0f766e" bg="#e6f4f1">ページ</Badge>}</td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{m.title}</span>
                    {m.status === 'draft' && <Badge color="#6b7280" bg="#f3f4f6">下書き</Badge>}
                    {m.status === 'archived' && <Badge color="#6b7280" bg="#f3f4f6">アーカイブ</Badge>}
                    {m.excerpt && <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{m.excerpt}</div>}
                  </td>
                  <td style={{ fontSize: 12.5 }}>{m.category ?? ''}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {m.tags.map(t => <Badge key={t}>{t}</Badge>)}
                    </div>
                  </td>
                  <td style={{ fontSize: 12.5 }}>v{m.version}</td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(m.updated_at)}<div style={{ color: '#6b7280' }}>{m.updated_by_name ?? ''}</div></td>
                  <td>
                    {m.require_read === 1 && !m.my_read
                      ? <Badge color="#dc2626" bg="#fee2e2">要確認</Badge>
                      : m.my_read ? <span style={{ fontSize: 12, color: '#15803d' }}>既読</span> : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 詳細 */}
      {detail && (
        <Modal width={760} onClose={() => setDetail(null)} title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {detail.kind === 'file' ? <FileText size={16} color="#7c3aed" /> : <BookOpen size={16} color="#0f766e" />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{detail.title}</span>
            <Badge>v{detail.version}</Badge>
          </span>
        }>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span>{detail.category ?? 'カテゴリなし'}</span>
            <span>更新: {fmtDateTime(detail.updated_at)}（{detail.updated_by_name ?? ''}）</span>
            {detail.tags.map(t => <Badge key={t}>{t}</Badge>)}
          </div>

          {detail.kind === 'file' ? (
            <div style={{ marginBottom: 14 }}>
              <a href={fileUrl(`/portal/manuals/${detail.id}/file`)} target="_blank" rel="noreferrer"
                className="btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                <Eye size={14} /> ファイルを開く（{detail.file_name}）
              </a>
            </div>
          ) : (
            <div style={{ background: '#fcfcfd', border: '1px solid #f3f4f6', borderRadius: 10, padding: '16px 20px', marginBottom: 14 }}>
              <MarkdownView text={detail.body ?? ''} />
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {detail.my_read ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                <CheckCircle2 size={15} /> 既読
              </span>
            ) : (
              <button className="btn-p" onClick={() => void markRead(detail)}>読みました（既読にする）</button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {isAdmin && (
                <button className="btn" onClick={async () => {
                  if (reads) { setReads(null); return; }
                  const r = await api.get<{ data: Array<{ user_id: string; user_name: string; read_at: string | null }> }>(`/portal/manuals/${detail.id}/reads`);
                  setReads(r.data ?? []);
                }}>既読状況 {detail.read_count ?? 0}/{detail.user_count ?? 0}</button>
              )}
              {isAdmin && detail.kind === 'wiki' && <button className="btn" onClick={() => { setEditor({ manual: detail }); setDetail(null); }}>編集</button>}
              {isAdmin && detail.kind === 'file' && <FileReplace manualId={detail.id} onDone={() => void openDetail(detail.id)} />}
              {isAdmin && (
                <button className="btn" style={{ color: '#dc2626' }} onClick={async () => {
                  if (!confirm('このマニュアルをアーカイブしますか？')) return;
                  try { await api.del(`/portal/manuals/${detail.id}`); setDetail(null); void load(); }
                  catch (e: unknown) { alert(e instanceof Error ? e.message : '失敗しました'); }
                }}>アーカイブ</button>
              )}
            </div>
          </div>

          {reads && (
            <div style={{ marginTop: 14, borderTop: '1px solid #f3f4f6', paddingTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {reads.map(r => (
                <Badge key={r.user_id} color={r.read_at ? '#15803d' : '#991b1b'} bg={r.read_at ? '#dcfce7' : '#fee2e2'}>
                  {r.user_name}{r.read_at ? '' : '（未読）'}
                </Badge>
              ))}
            </div>
          )}
        </Modal>
      )}

      {editor && (
        <WikiEditor manual={editor.manual} categories={categories}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); void load(); loadTags(); }} />
      )}

      {showFileUp && (
        <FileUploadModal categories={categories}
          onClose={() => setShowFileUp(false)}
          onSaved={() => { setShowFileUp(false); void load(); loadTags(); }} />
      )}
    </div>
  );
}

function FileReplace({ manualId, onDone }: { manualId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="btn" style={{ cursor: 'pointer' }}>
      {busy ? '差し替え中...' : 'ファイル差し替え'}
      <input type="file" style={{ display: 'none' }} disabled={busy} onChange={async e => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
          const form = new FormData();
          form.append('file', f);
          await api.postForm(`/portal/manuals/${manualId}/file`, form);
          onDone();
        } catch (err: unknown) { alert(err instanceof Error ? err.message : '失敗しました'); }
        finally { setBusy(false); e.target.value = ''; }
      }} />
    </label>
  );
}

function WikiEditor({ manual, categories, onClose, onSaved }: {
  manual: Manual | null; categories: string[]; onClose: () => void; onSaved: () => void;
}) {
  const [title, setTitle] = useState(manual?.title ?? '');
  const [body, setBody] = useState(manual?.body ?? '');
  const [tagsText, setTagsText] = useState((manual?.tags ?? []).join(', '));
  const [category, setCategory] = useState(manual?.category ?? '');
  const [requireRead, setRequireRead] = useState(manual?.require_read === 1);
  const [status, setStatus] = useState(manual?.status ?? 'published');
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { alert('タイトルを入力してください'); return; }
    setSaving(true);
    try {
      const payload = {
        title, body, category: category || null,
        tags: tagsText.split(/[、,]/).map(s => s.trim()).filter(Boolean),
        require_read: requireRead, status,
      };
      if (manual) await api.put(`/portal/manuals/${manual.id}`, payload);
      else await api.post('/portal/manuals', payload);
      onSaved();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <Modal width={820} title={manual ? `ページの編集（v${manual.version} → v${manual.version + 1}）` : 'ページを作成'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>タイトル *</label>
            <input style={fieldStyle} value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>カテゴリ</label>
            <input style={fieldStyle} value={category} onChange={e => setCategory(e.target.value)} list="portal-manual-categories" placeholder="例: 社内規程" />
            <datalist id="portal-manual-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={labelStyle}>本文（見出しは # ・箇条書きは - が使えます）</label>
            <button className="btn" style={{ fontSize: 12 }} onClick={() => setPreview(p => !p)}>{preview ? '編集に戻る' : 'プレビュー'}</button>
          </div>
          {preview ? (
            <div style={{ border: '1.5px solid #D1D5DB', borderRadius: 8, padding: '12px 16px', minHeight: 260, background: '#fcfcfd' }}>
              <MarkdownView text={body} />
            </div>
          ) : (
            <textarea style={{ ...fieldStyle, minHeight: 260, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              value={body} onChange={e => setBody(e.target.value)} />
          )}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>タグ（カンマ区切り）</label>
            <input style={fieldStyle} value={tagsText} onChange={e => setTagsText(e.target.value)} placeholder="例: 規程, 人事" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>公開状態</label>
            <select style={fieldStyle} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="published">公開</option>
              <option value="draft">下書き</option>
            </select>
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={requireRead} onChange={e => setRequireRead(e.target.checked)} />
          既読確認を求める（全員に「要確認」バッジが出ます）
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={saving} onClick={() => void save()}>{saving ? '保存中...' : '保存する'}</button>
        </div>
      </div>
    </Modal>
  );
}

function FileUploadModal({ categories, onClose, onSaved }: { categories: string[]; onClose: () => void; onSaved: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [category, setCategory] = useState('');
  const [requireRead, setRequireRead] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!file) { alert('ファイルを選んでください'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (title.trim()) form.append('title', title.trim());
      form.append('tags', tagsText.split(/[、,]/).map(s => s.trim()).filter(Boolean).join(','));
      if (category) form.append('category', category);
      form.append('require_read', requireRead ? '1' : '0');
      await api.postForm('/portal/manuals/file', form);
      onSaved();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : 'アップロードに失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Modal width={560} title="ファイルを登録（PDF・Word・Excel等）" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={labelStyle}>ファイル *（20MBまで。PDF・Wordは中身も全文検索できます）</label>
          <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} />
        </div>
        <div>
          <label style={labelStyle}>タイトル（空ならファイル名）</label>
          <input style={fieldStyle} value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>タグ（カンマ区切り）</label>
            <input style={fieldStyle} value={tagsText} onChange={e => setTagsText(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>カテゴリ</label>
            <input style={fieldStyle} value={category} onChange={e => setCategory(e.target.value)} list="portal-file-categories" />
            <datalist id="portal-file-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={requireRead} onChange={e => setRequireRead(e.target.checked)} /> 既読確認を求める
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={() => void save()}>{busy ? 'アップロード中...' : '登録する'}</button>
        </div>
      </div>
    </Modal>
  );
}
